import { Injectable } from '@angular/core';

import {
  CRUD_ENTITY_CONFIGS,
  CRUD_ENTITY_ORDER,
  type CrudEntityKey,
  type CrudRecord
} from '../config/entity-config';

type AppData = Record<CrudEntityKey, CrudRecord[]>;

@Injectable({ providedIn: 'root' })
export class DataStoreService {
  private readonly storageKey = 'angular-demo-data';

  constructor() {
    this.ensureSeedData();
  }

  getRecords(entity: CrudEntityKey): CrudRecord[] {
    return this.readData()[entity].map((record) => ({ ...record }));
  }

  addRecord(entity: CrudEntityKey, values: Record<string, string>): void {
    const currentData = this.readData();
    const nextRecord: CrudRecord = {
      id: this.generateId(entity),
      ...values
    };

    currentData[entity] = [nextRecord, ...currentData[entity]];
    this.writeData(currentData);
  }

  updateRecord(entity: CrudEntityKey, recordId: string, values: Record<string, string>): void {
    const currentData = this.readData();

    currentData[entity] = currentData[entity].map((record) =>
      record.id === recordId
        ? {
            ...record,
            ...values,
            id: recordId
          }
        : record
    );

    this.writeData(currentData);
  }

  deleteRecord(entity: CrudEntityKey, recordId: string): void {
    const currentData = this.readData();
    currentData[entity] = currentData[entity].filter((record) => record.id !== recordId);
    this.writeData(currentData);
  }

  getCollectionCount(entity: CrudEntityKey): number {
    return this.readData()[entity].length;
  }

  resetData(): void {
    this.writeData(this.createSeedData());
  }

  private ensureSeedData(): void {
    const existingData = localStorage.getItem(this.storageKey);

    if (!existingData) {
      this.writeData(this.createSeedData());
    }
  }

  private readData(): AppData {
    const rawValue = localStorage.getItem(this.storageKey);

    if (!rawValue) {
      const seedData = this.createSeedData();
      this.writeData(seedData);
      return seedData;
    }

    try {
      return JSON.parse(rawValue) as AppData;
    } catch {
      const seedData = this.createSeedData();
      this.writeData(seedData);
      return seedData;
    }
  }

  private writeData(data: AppData): void {
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  private createSeedData(): AppData {
    return CRUD_ENTITY_ORDER.reduce((collection, entityKey) => {
      collection[entityKey] = CRUD_ENTITY_CONFIGS[entityKey].seedData.map((record) => ({ ...record }));
      return collection;
    }, {} as AppData);
  }

  private generateId(entity: CrudEntityKey): string {
    const prefixMap: Record<CrudEntityKey, string> = {
      customers: 'C',
      orders: 'O',
      inventory: 'I',
      users: 'U',
      products: 'P'
    };

    return `${prefixMap[entity]}${Date.now()}`;
  }
}